"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { 
  X, 
  Save, 
  Settings, 
  Eye, 
  EyeOff, 
  Loader2,
  Package,
  Info,
  Layers,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  CheckCircle2
} from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Switch } from "../../../components/ui/switch"
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card"
import { Badge } from "../../../components/ui/badge"
import { useToast } from "../../../components/ui/use-toast"

const PRODUCT_FORM_CONFIG = {
  sections: {
    basic: {
      id: 'basic',
      name: 'Información Básica',
      icon: Info,
      visible: true,
      fields: {
        name: { label: 'Nombre del Producto', required: true, visible: true },
        sku: { label: 'SKU', required: true, visible: true },
        category_id: { label: 'Categoría', required: false, visible: true },
        price: { label: 'Precio de Venta', required: false, visible: true },
        cost: { label: 'Costo', required: false, visible: true },
        active: { label: 'Estado del Producto', required: false, visible: true },
        description: { label: 'Descripción', required: false, visible: true }
      }
    },
    details: {
      id: 'details',
      name: 'Detalles',
      icon: Package,
      visible: true,
      fields: {
        brand: { label: 'Marca', required: false, visible: true },
        model: { label: 'Modelo', required: false, visible: true },
        stock_quantity: { label: 'Cantidad en Stock', required: false, visible: true },
        min_stock: { label: 'Stock Mínimo', required: false, visible: true },
        unit: { label: 'Unidad de Medida', required: false, visible: true },
        warranty_period: { label: 'Período de Garantía', required: false, visible: true },
        weight: { label: 'Peso (kg)', required: false, visible: true },
        dimensions: { label: 'Dimensiones', required: false, visible: true },
        color: { label: 'Color', required: false, visible: true },
        material: { label: 'Material', required: false, visible: true },
        notes: { label: 'Notas Adicionales', required: false, visible: true }
      }
    },
    items: {
      id: 'items',
      name: 'Items Asociados',
      icon: Layers,
      visible: true,
      fields: {
        items_search: { label: 'Búsqueda de Items', required: false, visible: true },
        items_selection: { label: 'Selección de Items', required: false, visible: true }
      }
    }
  }
}

export function ProductConfigModal({ clientId, onClose, embedded = false }) {
  const [config, setConfig] = useState(PRODUCT_FORM_CONFIG)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showSuccessIcon, setShowSuccessIcon] = useState(false)
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    details: true,
    items: true
  })
  
  const { toast } = useToast()

  // Load configuration from localStorage on component mount
  useEffect(() => {
    const savedConfigString = localStorage.getItem(`product_form_config_${clientId}`);
    if (savedConfigString) {
      try {
        const savedOverwrites = JSON.parse(savedConfigString);
        
        // Create a new config that starts with PRODUCT_FORM_CONFIG (to keep function refs)
        // and then deeply merges the saved overwrites.
        const newConfig = {
          ...PRODUCT_FORM_CONFIG,
          sections: Object.fromEntries(
            Object.entries(PRODUCT_FORM_CONFIG.sections).map(([sectionId, defaultSection]) => {
              const savedSection = savedOverwrites.sections?.[sectionId];
              
              // Start with the default section (which includes the correct icon function)
              let mergedSection = { ...defaultSection };

              if (savedSection) {
                // Overwrite properties like 'visible' from savedSection
                // but explicitly keep the icon from defaultSection
                mergedSection = {
                  ...defaultSection, // Ensures icon and other structure is from default
                  ...savedSection,   // Applies saved properties like 'visible'
                  icon: defaultSection.icon, // Explicitly keep original icon
                };

                // Merge fields
                const newFields = Object.fromEntries(
                  Object.entries(defaultSection.fields).map(([fieldId, defaultField]) => {
                    const savedField = savedSection.fields?.[fieldId];
                    if (!savedField) return [fieldId, defaultField];
                    
                    return [fieldId, { ...defaultField, ...savedField }];
                  })
                );
                mergedSection.fields = newFields;
              }
              
              return [sectionId, mergedSection];
            })
          )
        };
        setConfig(newConfig);
      } catch (error) {
        console.error('Error applying saved configuration:', error);
        // Fallback to default if there's an error with saved data
        setConfig(PRODUCT_FORM_CONFIG);
      }
    } else {
      // No saved config, initialize with default (already handled by useState)
      // setConfig(PRODUCT_FORM_CONFIG); // This line is not strictly necessary due to useState default
    }
  }, [clientId]);

  const handleSectionVisibilityChange = (sectionId, visible) => {
    setConfig(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionId]: {
          ...prev.sections[sectionId],
          visible
        }
      }
    }))
  }

  const handleFieldVisibilityChange = (sectionId, fieldId, visible) => {
    setConfig(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionId]: {
          ...prev.sections[sectionId],
          fields: {
            ...prev.sections[sectionId].fields,
            [fieldId]: {
              ...prev.sections[sectionId].fields[fieldId],
              visible
            }
          }
        }
      }
    }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setShowSuccessIcon(false)
      
      // Save to localStorage (you can extend this to save to API)
      localStorage.setItem(`product_form_config_${clientId}`, JSON.stringify(config))
      
      toast({
        title: "Configuración guardada",
        description: "La configuración del formulario de productos ha sido guardada exitosamente.",
      })
      
      setShowSuccessIcon(true)
      setTimeout(() => {
        setShowSuccessIcon(false)
        if (!embedded) {
          onClose()
        }
      }, 2000)

      if (!embedded) {
        onClose()
      }
    } catch (error) {
      console.error('Error saving configuration:', error)
      toast({
        title: "Error",
        description: "No se pudo guardar la configuración. Intenta nuevamente.",
        variant: "destructive"
      })
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setConfig(PRODUCT_FORM_CONFIG)
    localStorage.removeItem(`product_form_config_${clientId}`)
    toast({
      title: "Configuración restablecida",
      description: "La configuración ha sido restablecida a los valores por defecto.",
    })
  }

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }

  const getVisibleFieldsCount = (section) => {
    return Object.values(section.fields).filter(field => field.visible).length
  }

  const getTotalFieldsCount = (section) => {
    return Object.values(section.fields).length
  }

  const content = (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 flex items-center">
            <Package className="h-5 w-5 mr-2 text-orange-500" />
            Configuración del Formulario de Productos
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Personaliza qué secciones y campos se muestran en el modal de creación/edición de productos
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            className="flex items-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Restablecer
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || showSuccessIcon}
            size="sm"
            className={`bg-orange-500 hover:bg-orange-600 text-white flex items-center gap-2 ${showSuccessIcon ? 'bg-green-500 hover:bg-green-600' : ''}`}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : showSuccessIcon ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {showSuccessIcon ? 'Guardado' : 'Guardar'}
          </Button>
        </div>
      </div>

      {/* Configuration Cards */}
      <div className="space-y-4">
        {Object.entries(config.sections).map(([sectionId, section]) => {
          const IconComponent = section.icon
          const isExpanded = expandedSections[sectionId]
          const visibleFields = getVisibleFieldsCount(section)
          const totalFields = getTotalFieldsCount(section)
          
          return (
            <Card key={sectionId} className="border border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleSection(sectionId)}
                      className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                    <IconComponent className="h-5 w-5 text-orange-500" />
                    <div>
                      <CardTitle className="text-base font-medium">
                        {section.name}
                      </CardTitle>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {visibleFields} de {totalFields} campos visibles
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Badge 
                      variant={section.visible ? "default" : "secondary"}
                      className={section.visible ? "bg-green-100 text-green-800" : ""}
                    >
                      {section.visible ? "Visible" : "Oculta"}
                    </Badge>
                    <Switch
                      checked={section.visible}
                      onCheckedChange={(checked) => handleSectionVisibilityChange(sectionId, checked)}
                    />
                  </div>
                </div>
              </CardHeader>
              
              {isExpanded && (
                <CardContent className="pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(section.fields).map(([fieldId, field]) => (
                      <div
                        key={fieldId}
                        className={`flex items-center justify-between p-3 rounded border transition-colors ${
                          field.visible 
                            ? 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900' 
                            : 'border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          {field.visible ? (
                            <Eye className="h-4 w-4 text-green-500" />
                          ) : (
                            <EyeOff className="h-4 w-4 text-slate-400" />
                          )}
                          <div>
                            <span className={`text-sm font-medium ${
                              field.visible ? 'text-slate-800 dark:text-slate-200' : 'text-slate-500 dark:text-slate-400'
                            }`}>
                              {field.label}
                            </span>
                            {field.required && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                Requerido
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Switch
                          checked={field.visible}
                          onCheckedChange={(checked) => handleFieldVisibilityChange(sectionId, fieldId, checked)}
                          disabled={field.required} // Required fields cannot be hidden
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )
        })}
      </div>

      {/* Summary */}
      <Card className="bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800">
        <CardContent className="p-4">
          <div className="flex items-center space-x-2 text-orange-800 dark:text-orange-200">
            <Settings className="h-4 w-4" />
            <span className="font-medium">Resumen de Configuración</span>
          </div>
          <div className="mt-2 text-sm text-orange-700 dark:text-orange-300">
            <p>
              • Secciones visibles: {Object.values(config.sections).filter(s => s.visible).length} de {Object.values(config.sections).length}
            </p>
            <p>
              • Total de campos visibles: {Object.values(config.sections).reduce((acc, section) => acc + getVisibleFieldsCount(section), 0)}
            </p>
            <p className="mt-2 text-xs">
              Los campos marcados como "Requerido" no pueden ser ocultados y siempre aparecerán en el formulario.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )

  if (embedded) {
    return content
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="bg-white dark:bg-slate-900 rounded shadow-lg w-[1000px] max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center">
            <Settings className="h-5 w-5 mr-2 text-orange-500" />
            Configuración de Productos
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {content}
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || showSuccessIcon}
            className={`text-white flex items-center gap-2 ${showSuccessIcon ? 'bg-green-500 hover:bg-green-600' : 'bg-orange-500 hover:bg-orange-600'}`}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : showSuccessIcon ? (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {showSuccessIcon ? 'Guardado!' : 'Guardar Configuración'}
          </Button>
        </div>
      </motion.div>
    </div>
  )
} 
